import { useMemo, useState } from "react"
import Modal from "./Modal"
import Icon from "../../components/Icon"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { formatMoney } from "../../utils/format"
import AutoInvoiceSuccessModal from "./AutoInvoiceSuccessModal"

const inputClass =
  "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

// Custom styled dropdown for picking a category. Avoids the browser-native
// <select> which renders inconsistently across platforms.
function CategoryDropdown({ categories, selectedId, mode, onSelectExisting, onSelectNew }) {
  const [open, setOpen] = useState(false)
  const selectedCat = categories.find((c) => String(c._id) === String(selectedId))

  const triggerLabel =
    mode === "new"
      ? "New category"
      : selectedCat?.name || (categories.length ? "Select a category" : "No categories yet")

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:border-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
      >
        <span
          className={
            mode === "new"
              ? "flex items-center gap-1.5 font-semibold text-teal-700"
              : "font-medium text-slate-800"
          }
        >
          {mode === "new" && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-100">
              <Icon name="plus-lg" size="0.55rem" className="text-teal-600" />
            </span>
          )}
          {triggerLabel}
        </span>
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size="0.7rem"
          className="shrink-0 text-slate-400"
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            {categories.length > 0 && (
              <div className="max-h-44 overflow-y-auto">
                {categories.map((cat) => {
                  const isSelected = String(selectedId) === String(cat._id) && mode !== "new"
                  return (
                    <button
                      key={cat._id}
                      type="button"
                      onClick={() => {
                        onSelectExisting(cat._id)
                        setOpen(false)
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected ? "bg-teal-50" : "hover:bg-slate-50"
                      }`}
                    >
                      {isSelected ? (
                        <Icon name="check2" size="0.75rem" className="shrink-0 text-teal-600" />
                      ) : (
                        <span className="w-3 shrink-0" />
                      )}
                      <span className="flex-1 font-medium text-slate-800">{cat.name}</span>
                      {cat.autoInvoiceEnabled && (
                        <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                          auto on
                        </span>
                      )}
                      {!cat.property && (
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                          company
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            <div className={categories.length > 0 ? "border-t border-slate-100" : ""}>
              <button
                type="button"
                onClick={() => {
                  onSelectNew()
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100">
                  <Icon name="plus-lg" size="0.65rem" className="text-teal-600" />
                </span>
                Create new category…
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function SetupForm({ categories, editingCategory, onCreateCategory, onConfigure, providerId, onBack, onSuccess }) {
  const billableCategories = useMemo(
    () => (categories || []).filter((c) => c.kind !== "water"),
    [categories]
  )
  const hasExisting = billableCategories.length > 0

  const seedCategory = editingCategory || (hasExisting ? billableCategories[0] : null)
  const [mode, setMode] = useState(editingCategory ? "existing" : hasExisting ? "existing" : "new")
  const [selectedCategoryId, setSelectedCategoryId] = useState(seedCategory?._id || "")
  const [newName, setNewName] = useState("")
  const [amount, setAmount] = useState(
    seedCategory?.autoInvoiceEnabled ? String(seedCategory.autoInvoiceAmount || "") : ""
  )
  const [dayOfMonth, setDayOfMonth] = useState(
    seedCategory?.autoInvoiceEnabled ? Number(seedCategory.autoInvoiceDayOfMonth || 1) : 1
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const pickCategory = (id) => {
    setSelectedCategoryId(id)
    const cat = billableCategories.find((c) => String(c._id) === String(id))
    if (cat?.autoInvoiceEnabled) {
      setAmount(String(cat.autoInvoiceAmount || ""))
      setDayOfMonth(Number(cat.autoInvoiceDayOfMonth || 1))
    } else {
      setAmount("")
      setDayOfMonth(1)
    }
  }

  const submit = async (event) => {
    event.preventDefault()
    if (submitting) return
    const numericAmount = Number(amount)
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("Enter a monthly amount greater than zero.")
      return
    }
    if (mode === "new" && !newName.trim()) {
      setError("Enter a name for the new category.")
      return
    }
    setSubmitting(true)
    setError("")
    try {
      let categoryId = editingCategory ? editingCategory._id : selectedCategoryId
      let categoryName = editingCategory?.name || billableCategories.find(
        (category) => String(category._id) === String(selectedCategoryId)
      )?.name || "Utility bill"
      if (mode === "new") {
        const created = await onCreateCategory(providerId, { name: newName.trim(), kind: "generic" })
        categoryId = created?._id
        if (!categoryId) throw new Error("Could not create the category.")
        categoryName = created?.name || newName.trim()
      }
      const result = await onConfigure(providerId, categoryId, {
        enabled: true,
        amount: numericAmount,
        dayOfMonth: Number(dayOfMonth),
      })
      onSuccess({
        categoryName,
        amount: numericAmount,
        dayOfMonth: Number(dayOfMonth),
        run: result?.run || null,
      })
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to set up auto-invoicing."))
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
        >
          <Icon name="arrow-left" size="0.7rem" />
          Back to list
        </button>
      )}
      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
      )}
      <form onSubmit={submit} className="space-y-3">
        {editingCategory ? (
          <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <Icon name="arrow-repeat" size="0.9rem" className="shrink-0 text-teal-600" />
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Editing</p>
              <p className="truncate text-sm font-black text-slate-800">{editingCategory.name}</p>
            </div>
          </div>
        ) : (
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Category</label>
            <CategoryDropdown
              categories={billableCategories}
              selectedId={selectedCategoryId}
              mode={mode}
              onSelectExisting={(id) => {
                setMode("existing")
                pickCategory(id)
              }}
              onSelectNew={() => setMode("new")}
            />
            {mode === "new" && (
              <input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                className={`${inputClass} mt-2`}
                placeholder="Category name (e.g. Trash)"
                autoFocus
              />
            )}
            <p className="mt-1.5 text-[11px] text-slate-400">Water categories cannot be auto-invoiced.</p>
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-slate-600">Monthly amount (KSh)</label>
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className={inputClass}
            placeholder="e.g. 100"
            required
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-slate-600">Bill on day of month</label>
          <div className="relative">
            <select
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(Number(event.target.value))}
              className={`${inputClass} appearance-none pr-8`}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <Icon
              name="chevron-down"
              size="0.7rem"
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Day 29–31 falls on the last day of shorter months. Every active customer is billed automatically.
          </p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
        >
          {submitting ? "Saving…" : editingCategory ? "Save changes" : "Turn on auto-invoicing"}
        </button>
      </form>
    </div>
  )
}

function AutoBillModal({ providerId, providerName, categories = [], onCreateCategory, onConfigure, onClose }) {
  const billableCategories = useMemo(
    () => (categories || []).filter((c) => c.kind !== "water"),
    [categories]
  )
  const activeBillings = useMemo(
    () => billableCategories.filter((c) => c.autoInvoiceEnabled),
    [billableCategories]
  )
  const hasActiveBillings = activeBillings.length > 0

  const [view, setView] = useState(hasActiveBillings ? "list" : "form")
  const [editingCategory, setEditingCategory] = useState(null)
  const [formKey, setFormKey] = useState(0)
  const [confirmDisableId, setConfirmDisableId] = useState(null)
  const [disabling, setDisabling] = useState(false)
  const [disableError, setDisableError] = useState("")
  const [success, setSuccess] = useState(null)

  const goToForm = (category = null) => {
    setEditingCategory(category)
    setFormKey((k) => k + 1)
    setView("form")
  }

  const goToList = () => {
    setEditingCategory(null)
    setView("list")
  }

  const handleDisable = async (categoryId) => {
    const remaining = activeBillings.filter((c) => String(c._id) !== String(categoryId))
    setDisabling(true)
    setDisableError("")
    try {
      await onConfigure(providerId, categoryId, { enabled: false })
      setConfirmDisableId(null)
      if (remaining.length === 0) {
        onClose()
      }
    } catch (err) {
      setDisableError(getApiErrorMessage(err, "Failed to turn off auto-billing."))
      setDisabling(false)
    }
  }

  const modalTitle =
    view === "list"
      ? "Auto-billing"
      : editingCategory
      ? `Edit · ${editingCategory.name}`
      : "Set up auto invoicing"

  if (success) {
    return (
      <AutoInvoiceSuccessModal
        categoryName={success.categoryName}
        providerName={providerName}
        amount={success.amount}
        dayOfMonth={success.dayOfMonth}
        run={success.run}
        onClose={onClose}
      />
    )
  }

  return (
    <Modal
      title={modalTitle}
      subtitle={providerName ? `Monthly billing · ${providerName}` : "Monthly billing"}
      icon="arrow-repeat"
      onClose={onClose}
    >
      {view === "list" ? (
        <div className="space-y-2">
          {disableError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
              {disableError}
            </p>
          )}

          {activeBillings.map((category) => (
            <div key={category._id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              {confirmDisableId === category._id ? (
                <div className="bg-red-50 p-3">
                  <p className="mb-1 text-xs font-bold text-red-700">
                    Turn off <span className="font-black">{category.name}</span>?
                  </p>
                  <p className="mb-3 text-[11px] text-red-500">
                    Invoices will stop generating. Existing ones are not affected.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmDisableId(null)
                        setDisableError("")
                      }}
                      disabled={disabling}
                      className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDisable(category._id)}
                      disabled={disabling}
                      className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                    >
                      {disabling ? "Turning off…" : "Yes, turn off"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                    <Icon name="arrow-repeat" size="0.85rem" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-black text-slate-800">{category.name}</p>
                    <p className="text-[11px] text-slate-500">
                      {formatMoney(category.autoInvoiceAmount)} /mo · Day {category.autoInvoiceDayOfMonth}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      onClick={() => goToForm(category)}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 transition-colors hover:bg-slate-100"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmDisableId(category._id)
                        setDisableError("")
                      }}
                      className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-bold text-red-600 transition-colors hover:bg-red-100"
                    >
                      Turn off
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => goToForm(null)}
            className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-teal-300 bg-teal-50 px-3 py-2.5 text-xs font-bold text-teal-700 transition-colors hover:bg-teal-100"
          >
            <Icon name="plus-lg" size="0.75rem" />
            Set up another category
          </button>
        </div>
      ) : (
        <SetupForm
          key={formKey}
          categories={categories}
          editingCategory={editingCategory}
          onCreateCategory={onCreateCategory}
          onConfigure={onConfigure}
          providerId={providerId}
          onBack={hasActiveBillings ? goToList : null}
          onSuccess={setSuccess}
        />
      )}
    </Modal>
  )
}

export default AutoBillModal
